/**
 * LIFF wrapper — typed access to the LINE LIFF SDK, with a dev mock when not
 * running inside LINE (VITE_LIFF_ID empty or DEV).
 *
 * login() uses redirectUri = window.location.href so the full route (search +
 * hash) survives the OAuth round-trip — the vanilla app dropped the hash and
 * caused a login loop (ADR 0002 risk #1).
 */
import liff from '@line/liff';

export const LIFF_ID = (import.meta.env.VITE_LIFF_ID ?? '').trim();
export const LIFF_URL = LIFF_ID ? `https://liff.line.me/${LIFF_ID}` : '';
const USE_MOCK = !LIFF_ID || import.meta.env.DEV;

interface LiffShim {
  isLoggedIn: () => boolean;
  isInClient: () => boolean;
  login: (opts?: { redirectUri?: string }) => void;
  logout: () => void;
  getIDToken: () => string | null;
  getProfile: () => Promise<{ userId: string; displayName: string; pictureUrl?: string }>;
  closeWindow: () => void;
  scanCodeV2?: () => Promise<{ value: string }>;
}

const mockProfile = {
  userId: 'U_mock_dev_user',
  displayName: 'Dev User (Mock)',
  pictureUrl: undefined,
};

const mockShim: LiffShim = {
  isLoggedIn: () => true,
  isInClient: () => false,
  login: () => console.log('[mock] login'),
  logout: () => console.log('[mock] logout'),
  getIDToken: () => 'mock_id_token_for_local_dev',
  getProfile: async () => mockProfile,
  closeWindow: () => console.log('[mock] closeWindow'),
};

let activeShim: LiffShim = mockShim;
let initialized = false;

export async function initLiff(): Promise<void> {
  if (initialized) return;

  if (USE_MOCK) {
    activeShim = mockShim;
    initialized = true;
    return;
  }

  // ห้ามใส่ withLoginOnExternalBrowser: true — option นี้ทำให้ liff.init() เอง
  // auto-redirect ไป LINE OAuth บนเบราว์เซอร์ทันที = เดสก์ท็อปเข้าหน้า user/pass
  // ไม่ได้ (per PayPilot pattern + memory feedback-liff-root-autologin-traps-browser).
  await liff.init({ liffId: LIFF_ID });
  if (liff.isInClient() && !liff.isLoggedIn()) {
    // เฉพาะในแอป LINE: ถ้ายังไม่ login (เปิดจากลิงก์ LIFF โดยตรง) ขอ login ครั้งเดียว
    // เพื่อให้ได้ idToken. preserve full route (search + hash) across the round-trip.
    liff.login({ redirectUri: window.location.href });
    return;
  }
  // บนเบราว์เซอร์: ไม่บังคับเด้ง LINE. ถ้าไม่ได้ login LINE → getIDToken()=null →
  // useAuth ตก 'no-liff' → หน้า PasswordLogin (user/pass หลัก, LINE เป็นตัวเลือก).
  activeShim = liff as unknown as LiffShim;
  initialized = true;
}

export function getIDToken(): string | null {
  // Guard: only trust the shim once initLiff has actually run (or in dev/mock),
  // so the web-login flow doesn't get a fake token and hit /me with garbage.
  if (!initialized && !USE_MOCK) return null;
  return activeShim.getIDToken();
}

export async function getProfile() {
  return activeShim.getProfile();
}

export function logout() {
  activeShim.logout();
}

export function login() {
  activeShim.login({ redirectUri: window.location.href });
}

export function isInClient(): boolean {
  return activeShim.isInClient();
}

export function closeWindow() {
  activeShim.closeWindow();
}

export async function scanQR(): Promise<string | null> {
  if (activeShim.scanCodeV2) {
    const result = await activeShim.scanCodeV2();
    return result.value;
  }
  return null;
}

export const isMock = USE_MOCK;
